// IMPORTANT: I call PostHog in this file too. It's okay for now, because I don't know which tool will be better in the future, so both are used simultaneously.
import posthog from "posthog-js";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
import type { DTOMenuIndicatorResponse, DTOEngagementType } from "vue-app/api/dto";
// import { EngagementsDTO, getEngagements } from "vue-app/api/engagements/engagements";
import { EdroneContext } from "vue-app/types/edroneContext";
import axios from "axios";

export async function getActiveReadyFlows(): Promise<DTOEngagementType[]> {
  const { data } = await axios.get("/json/engagements/active-types");
  return data;
  // const hasEngagementPermission = Boolean(window.EDRONE_CONTEXT?.user?.roles?.["ROLE_ENGAGEMENT"]);

  // if (!hasEngagementPermission) {
  //   return [];
  // }

  // const engagementsData = await getEngagements();
  // const excludedAutomations: DTOEngagementType[] = ["NEWSLETTER_HUB", "CUSTOM_FLOW", "PUSH_NEWSLETTER"];
  // const activeEngagements: DTOEngagementType[] = [];

  // const transactionalEngagements =
  //   engagementsData.transactionalEngagements as EngagementsDTO["transactionalEngagements"];

  // for (const engagement of Object.values(transactionalEngagements)) {
  //   if (!excludedAutomations.includes(engagement.engagementType) && engagement.engagementStatus === "ACTIVE") {
  //     activeEngagements.push(engagement.engagementType);
  //   }
  // }

  // return activeEngagements;
}

export function amplitudeSendEvent(
  eventName: string,
  parameters?: Record<string, string | number | boolean | number[] | string[]>
) {
  window.amplitude?.track(eventName, {
    is_paying: window.EDRONE_CONTEXT?.tracker?.isPaying || false,
    isMobileApp: ReactNativeBridge.checkIsWebViewMode(),
    ...getFlattenedEdroneContext(),
    ...parameters,
  });

  posthog.capture(eventName, {
    ...parameters,
    isMobileApp: ReactNativeBridge.checkIsWebViewMode(),
  });

  // this is to support events sent to Google Analytics (the goal is to use Amplitude but we want to support both for now)
  if (typeof window.gtag === "function") {
    window.gtag?.("event", eventName.substring(0, 40), {
      ...parameters,
      ...getFlattenedEdroneContext(),
      is_paying: window.EDRONE_CONTEXT?.tracker?.isPaying || false,
      isMobileApp: ReactNativeBridge.checkIsWebViewMode(),
    });
  }
}

export function amplitudeSetUserProperties(properties: Record<string, unknown>) {
  if (!window.amplitude?.Identify) {
    console.warn("Amplitude Identify class not available");
    return;
  }

  const identifyEvent = new window.amplitude.Identify();

  identifyEvent.set("is_paying", window.EDRONE_CONTEXT?.tracker?.isPaying || false);
  identifyEvent.set("isMobileApp", ReactNativeBridge.checkIsWebViewMode());

  const flattenedContext = getFlattenedEdroneContext();
  Object.entries(flattenedContext).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });

  Object.entries(properties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });

  window.amplitude.identify(identifyEvent);
  posthog.setPersonProperties({
    ...properties,
  });
}

export async function amplitudeUpdateAutomationProperties(menuData: DTOMenuIndicatorResponse) {
  if (!menuData) return;

  try {
    const activeReadyFlows = await getActiveReadyFlows();
    const hasEngagementPermission = Boolean(window.EDRONE_CONTEXT?.user?.roles?.["ROLE_ENGAGEMENT"]);

    const automationProperties: Record<string, unknown> = {
      has_onsite_cross_sell_active: menuData.onsiteActivity?.[9] || false,
      has_onsite_popup_active: menuData.onsiteActivity?.[5] || false,
      has_onsite_slider_active: menuData.onsiteActivity?.[8] || false,
      has_onsite_banner_active: menuData.onsiteActivity?.[7] || false,
      has_onsite_social_proof_active: menuData.onsiteActivity?.[3] || false,
      has_chat_enabled: menuData.chatEnabled || false,
      has_newsletter_subscription: hasEngagementPermission
        ? activeReadyFlows.includes("NEWSLETTER_SUBSCRIPTION")
        : null,
      has_restore_customers: hasEngagementPermission ? activeReadyFlows.includes("RESTORE_CUSTOMERS") : null,
      has_advanced_restore_customers: hasEngagementPermission
        ? activeReadyFlows.includes("ADVANCED_RESTORE_CUSTOMERS")
        : null,
      has_abandoned_carts: hasEngagementPermission ? activeReadyFlows.includes("ABANDONED_CARTS") : null,
      has_loyalty_program: hasEngagementPermission ? activeReadyFlows.includes("LOYALTY_PROGRAM") : null,
      has_after_sale: hasEngagementPermission ? activeReadyFlows.includes("AFTER_SALE") : null,
      has_wish_list: hasEngagementPermission ? activeReadyFlows.includes("WISH_LIST") : null,
      has_recommendations: hasEngagementPermission ? activeReadyFlows.includes("RECOMMENDATIONS") : null,
      has_cross_selling_transactional: hasEngagementPermission
        ? activeReadyFlows.includes("CROSS_SELLING_TRANSACTIONAL")
        : null,
      has_chat_subscription: hasEngagementPermission ? activeReadyFlows.includes("CHAT_SUBSCRIPTION") : null,
      has_birthday: hasEngagementPermission ? activeReadyFlows.includes("BIRTHDAY") : null,
      has_refer_a_friend: hasEngagementPermission ? activeReadyFlows.includes("REFER_A_FRIEND") : null,
      has_sms_newsletter: hasEngagementPermission ? activeReadyFlows.includes("SMS_NEWSLETTER") : null,
      has_newsletter_sign_up_after_purchase: hasEngagementPermission
        ? activeReadyFlows.includes("NEWSLETTER_SIGN_UP_AFTER_PURCHASE")
        : null,
      has_price_change_up: hasEngagementPermission ? activeReadyFlows.includes("PRICE_CHANGE_UP") : null,
      has_price_change_down: hasEngagementPermission ? activeReadyFlows.includes("PRICE_CHANGE_DOWN") : null,
      has_newest_products: hasEngagementPermission ? activeReadyFlows.includes("NEWEST_PRODUCTS") : null,
      has_low_stock_products: hasEngagementPermission ? activeReadyFlows.includes("LOW_STOCK_PRODUCTS") : null,
      has_ask_for_review: hasEngagementPermission ? activeReadyFlows.includes("ASK_FOR_REVIEW") : null,
      has_back_in_stock: hasEngagementPermission ? activeReadyFlows.includes("BACK_IN_STOCK") : null,
    };

    amplitudeSetUserProperties(automationProperties);
  } catch (error) {
    console.error("Failed to update automation properties in Amplitude:", error);
  }
}

export function getFlattenedEdroneContext(): Record<string, unknown> {
  if (!window.EDRONE_CONTEXT) {
    return {};
  }

  const result: Record<string, unknown> = {};

  function flatten(obj: EdroneContext, prefix: string = "") {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (
          key === "trackerBranding" ||
          key === "trackerConfiguration" ||
          key === "trackerConfigurationFeatures" ||
          key === "configuration" ||
          key === "branding" ||
          key.includes("Configuration") ||
          key.includes("Branding")
        ) {
          continue;
        }

        const newKey = prefix ? `${prefix}_${key}` : key;

        if (obj[key] !== null && typeof obj[key] === "object" && !Array.isArray(obj[key])) {
          flatten(obj[key] as EdroneContext, newKey);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
  }

  flatten(window.EDRONE_CONTEXT);
  return result;
}
